// src/pages/admin/analytics/components/BulkWastageEditModal.jsx
import { useState, useEffect } from 'react';
import { X, CheckSquare, Save, AlertCircle, Edit3, ListOrdered, Trash2, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import analyticsService from '../../../../services/analyticsService';
import { WASTAGE_REASONS } from './EditWastageModal';

const formatForDateTimeInput = (dateInput) => {
  if (!dateInput) return '';
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const BulkWastageEditModal = ({ isOpen, onClose, onSuccess, selectedLogs = [], onClearSelection }) => {
  const [mode, setMode] = useState('batch'); // 'batch' (quick apply) | 'table' (grid edit)

  // Batch Mode States
  const [applyDate, setApplyDate] = useState(false);
  const [batchDate, setBatchDate] = useState('');
  const [applyReason, setApplyReason] = useState(false);
  const [batchReason, setBatchReason] = useState('trimming_prep');
  const [applyNotes, setApplyNotes] = useState(false);
  const [notesAction, setNotesAction] = useState('replace'); // 'replace' | 'append'
  const [batchNotes, setBatchNotes] = useState('');

  // Table Grid Mode States (cloned editable rows)
  const [editableRows, setEditableRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && selectedLogs.length > 0) {
      setEditableRows(
        selectedLogs.map((log) => ({
          id: log._id,
          itemName: log.itemType === 'raw_material' ? log.rawMaterial?.name : log.product?.name,
          itemType: log.itemType,
          itemCode: log.itemType === 'raw_material' ? log.rawMaterial?.code : log.product?.sku,
          branchName: log.branch?.name || 'N/A',
          quantity: log.quantity || 0,
          originalQuantity: log.quantity || 0,
          unitName: log.unitName || 'units',
          unitCost: log.unitCost || 0,
          reason: log.reason || 'trimming_prep',
          notes: log.notes || '',
          dateTime: formatForDateTimeInput(log.createdAt),
        }))
      );
      setApplyDate(false);
      setBatchDate(formatForDateTimeInput(selectedLogs[0]?.createdAt || new Date()));
      setApplyReason(false);
      setApplyNotes(false);
      setBatchNotes('');
    }
  }, [isOpen, selectedLogs]);

  if (!isOpen || selectedLogs.length === 0) return null;

  const handleRowChange = (id, field, value) => {
    setEditableRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleRemoveRow = (id) => {
    setEditableRows((prev) => prev.filter((row) => row.id !== id));
  };

  // Calculate total costs for the grid
  const totalCost = editableRows.reduce((sum, row) => {
    const qty = parseFloat(row.quantity) || 0;
    return sum + qty * (row.unitCost || 0);
  }, 0);

  // Apply batch form values to table grid
  const handleApplyBatchToGrid = () => {
    if (!applyDate && !applyReason && !applyNotes) {
      toast.error('Please select at least one field (Date, Reason, or Notes) to apply');
      return;
    }

    setEditableRows((prev) =>
      prev.map((row) => {
        let newReason = row.reason;
        let newNotes = row.notes;
        let newDate = row.dateTime;

        if (applyDate && batchDate) {
          newDate = batchDate;
        }
        if (applyReason) {
          newReason = batchReason;
        }
        if (applyNotes) {
          if (notesAction === 'append' && row.notes) {
            newNotes = `${row.notes} | ${batchNotes.trim()}`;
          } else {
            newNotes = batchNotes.trim();
          }
        }

        return { ...row, reason: newReason, notes: newNotes, dateTime: newDate };
      })
    );

    toast.success(`Applied changes to all ${editableRows.length} items in preview`);
    setMode('table');
  };

  // Submit all changes
  const handleSaveAll = async () => {
    if (editableRows.length === 0) {
      toast.error('No items selected for bulk edit');
      return;
    }

    // Validate quantities in grid
    for (const row of editableRows) {
      const q = parseFloat(row.quantity);
      if (isNaN(q) || q <= 0) {
        toast.error(`Invalid quantity for "${row.itemName || 'item'}". Must be greater than 0.`);
        return;
      }
    }

    try {
      setLoading(true);

      if (mode === 'batch' && (applyDate || applyReason || applyNotes)) {
        // Quick bulk apply mode
        const changes = {};
        if (applyDate && batchDate) {
          changes.createdAt = new Date(batchDate).toISOString();
        }
        if (applyReason) changes.reason = batchReason;
        if (applyNotes) {
          changes.notes = batchNotes.trim();
          changes.notesAction = notesAction;
        }

        await analyticsService.bulkUpdateWastage({
          ids: editableRows.map((r) => r.id),
          changes,
        });
      } else {
        // Item-by-item grid mode
        const itemsToUpdate = editableRows.map((r) => ({
          id: r.id,
          quantity: parseFloat(r.quantity),
          reason: r.reason,
          notes: r.notes.trim(),
          createdAt: r.dateTime ? new Date(r.dateTime).toISOString() : undefined,
        }));

        await analyticsService.bulkUpdateWastage({
          items: itemsToUpdate,
        });
      }

      toast.success(`Successfully updated ${editableRows.length} wastage entries!`);
      if (onSuccess) onSuccess();
      if (onClearSelection) onClearSelection();
      onClose();
    } catch (err) {
      console.error('Bulk update error:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to bulk update entries');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden border border-gray-100 flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-700 via-rose-700 to-orange-700 px-6 py-4 text-white flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/15 rounded-xl backdrop-blur-sm">
              <CheckSquare className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-black tracking-tight">Bulk Edit Wastage Logs</h3>
              <p className="text-xs text-rose-100">
                Updating {editableRows.length} selected wastage record(s)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 rounded-xl text-rose-100 hover:text-white hover:bg-white/20 transition-all"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Mode Selector Tabs */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-2.5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setMode('batch')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center space-x-1.5 ${
                mode === 'batch'
                  ? 'bg-rose-600 text-white shadow-sm'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <Edit3 className="h-3.5 w-3.5" />
              <span>Quick Apply to All</span>
            </button>
            <button
              type="button"
              onClick={() => setMode('table')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center space-x-1.5 ${
                mode === 'table'
                  ? 'bg-rose-600 text-white shadow-sm'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <ListOrdered className="h-3.5 w-3.5" />
              <span>Editable Grid Table ({editableRows.length})</span>
            </button>
          </div>

          <div className="text-xs text-gray-500 font-medium">
            Total Cost:{' '}
            <span className="font-bold text-gray-900">
              Rs. {totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-5">
          {mode === 'batch' ? (
            /* Quick Apply Mode */
            <div className="space-y-5">
              <div className="bg-rose-50/70 border border-rose-100 rounded-xl p-4 text-xs text-rose-900 flex items-start space-x-2">
                <AlertCircle className="h-4 w-4 text-rose-600 mt-0.5 flex-shrink-0" />
                <p>
                  Check the fields below you wish to overwrite or update across all{' '}
                  <span className="font-bold">{editableRows.length}</span> selected records. You can save directly or click &quot;Apply to Grid&quot; to review row-by-row before saving.
                </p>
              </div>

              {/* Batch Date */}
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                <label className="flex items-center space-x-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={applyDate}
                    onChange={(e) => setApplyDate(e.target.checked)}
                    className="w-4 h-4 text-rose-600 rounded border-gray-300 focus:ring-rose-500"
                  />
                  <span className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                    Update Date & Time for all selected records
                  </span>
                </label>

                {applyDate && (
                  <div className="pt-2 pl-6">
                    <input
                      type="datetime-local"
                      value={batchDate}
                      onChange={(e) => setBatchDate(e.target.value)}
                      className="w-full px-3.5 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all outline-none"
                    />
                  </div>
                )}
              </div>

              {/* Batch Reason */}
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                <label className="flex items-center space-x-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={applyReason}
                    onChange={(e) => setApplyReason(e.target.checked)}
                    className="w-4 h-4 text-rose-600 rounded border-gray-300 focus:ring-rose-500"
                  />
                  <span className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                    Update Reason for all selected records
                  </span>
                </label>

                {applyReason && (
                  <div className="pt-2 pl-6">
                    <select
                      value={batchReason}
                      onChange={(e) => setBatchReason(e.target.value)}
                      className="w-full px-3.5 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all outline-none"
                    >
                      {WASTAGE_REASONS.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Batch Notes */}
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                <label className="flex items-center space-x-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={applyNotes}
                    onChange={(e) => setApplyNotes(e.target.checked)}
                    className="w-4 h-4 text-rose-600 rounded border-gray-300 focus:ring-rose-500"
                  />
                  <span className="text-xs font-bold text-gray-800 uppercase tracking-wider">
                    Update Notes / Staff Remarks for all selected records
                  </span>
                </label>

                {applyNotes && (
                  <div className="pt-2 pl-6 space-y-3">
                    <div className="flex items-center space-x-4 text-xs font-semibold text-gray-600">
                      <label className="flex items-center space-x-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="notesAction"
                          value="replace"
                          checked={notesAction === 'replace'}
                          onChange={() => setNotesAction('replace')}
                          className="text-rose-600 focus:ring-rose-500"
                        />
                        <span>Replace existing notes</span>
                      </label>
                      <label className="flex items-center space-x-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="notesAction"
                          value="append"
                          checked={notesAction === 'append'}
                          onChange={() => setNotesAction('append')}
                          className="text-rose-600 focus:ring-rose-500"
                        />
                        <span>Append to existing notes</span>
                      </label>
                    </div>
                    <textarea
                      rows={2}
                      value={batchNotes}
                      onChange={(e) => setBatchNotes(e.target.value)}
                      placeholder="Enter note / remark to apply..."
                      className="w-full px-3.5 py-2 text-sm text-gray-800 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all outline-none resize-none"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleApplyBatchToGrid}
                  disabled={!applyDate && !applyReason && !applyNotes}
                  className="px-4 py-2 bg-gray-900 text-white text-xs font-bold rounded-xl hover:bg-gray-800 transition-all disabled:opacity-40"
                >
                  Apply to Grid & Preview &rarr;
                </button>
              </div>
            </div>
          ) : (
            /* Grid Table Mode */
            <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-gray-100 border-b border-gray-200 text-gray-600 font-semibold uppercase tracking-wider">
                    <tr>
                      <th className="p-3">Item</th>
                      <th className="p-3">Branch</th>
                      <th className="p-3 w-44">Date & Time</th>
                      <th className="p-3 w-28">Qty</th>
                      <th className="p-3 w-36">Reason</th>
                      <th className="p-3">Notes</th>
                      <th className="p-3 text-right">Cost (Rs.)</th>
                      <th className="p-3 text-center w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {editableRows.map((row) => {
                      const qty = parseFloat(row.quantity) || 0;
                      const rowCost = qty * (row.unitCost || 0);

                      return (
                        <tr key={row.id} className="hover:bg-gray-50/80 transition-colors">
                          <td className="p-3">
                            <p className="font-bold text-gray-900 truncate max-w-[140px]">
                              {row.itemName}
                            </p>
                            <span
                              className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${
                                row.itemType === 'raw_material'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-purple-100 text-purple-800'
                              }`}
                            >
                              {row.itemType === 'raw_material' ? 'RM' : 'Product'}
                            </span>
                          </td>
                          <td className="p-3 text-gray-600 text-[11px] whitespace-nowrap">{row.branchName}</td>
                          <td className="p-3">
                            <input
                              type="datetime-local"
                              value={row.dateTime}
                              onChange={(e) => handleRowChange(row.id, 'dateTime', e.target.value)}
                              className="w-full px-2 py-1 text-xs text-gray-900 border border-gray-300 rounded-lg focus:ring-1 focus:ring-rose-500 outline-none"
                            />
                          </td>
                          <td className="p-3">
                            <div className="relative">
                              <input
                                type="number"
                                step="any"
                                min="0.001"
                                value={row.quantity}
                                onChange={(e) => handleRowChange(row.id, 'quantity', e.target.value)}
                                className="w-full px-2 py-1 text-xs font-bold text-gray-900 border border-gray-300 rounded-lg focus:ring-1 focus:ring-rose-500 outline-none"
                              />
                              <span className="text-[10px] text-gray-400 font-semibold block mt-0.5">
                                {row.unitName}
                              </span>
                            </div>
                          </td>
                          <td className="p-3">
                            <select
                              value={row.reason}
                              onChange={(e) => handleRowChange(row.id, 'reason', e.target.value)}
                              className="w-full px-2 py-1 text-xs text-gray-900 border border-gray-300 rounded-lg focus:ring-1 focus:ring-rose-500 outline-none"
                            >
                              {WASTAGE_REASONS.map((r) => (
                                <option key={r.value} value={r.value}>
                                  {r.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="p-3">
                            <input
                              type="text"
                              value={row.notes}
                              onChange={(e) => handleRowChange(row.id, 'notes', e.target.value)}
                              placeholder="Remarks..."
                              className="w-full px-2 py-1 text-xs text-gray-800 border border-gray-300 rounded-lg focus:ring-1 focus:ring-rose-500 outline-none"
                            />
                          </td>
                          <td className="p-3 text-right font-black text-gray-900 whitespace-nowrap">
                            {rowCost.toFixed(2)}
                          </td>
                          <td className="p-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleRemoveRow(row.id)}
                              title="Remove from bulk edit"
                              className="text-gray-400 hover:text-red-600 p-1 transition-colors"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-xs font-bold text-gray-600 hover:bg-gray-200 rounded-xl transition-all"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSaveAll}
            disabled={loading || editableRows.length === 0}
            className="px-6 py-2.5 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-xs font-bold rounded-xl shadow-md hover:shadow-lg transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" />
            <span>{loading ? 'Saving Changes...' : `Save Changes (${editableRows.length} Items)`}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkWastageEditModal;
